import { useReactiveVar } from "@apollo/client";
import { useNavigate, useRouterState, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import {
  CATEGORY_ALL,
  CATEGORY_ESCAPE_ROOM,
  CATEGORY_HAUNT,
} from "@/components/game-categories/constants";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import useAnalytics from "@/hooks/use-analytics";
import useAuth from "@/hooks/use-auth";
import { exploreCategory, exploreCategoryModal } from "@/store";

import { BothIcon, EscapeRoomsIcon, HauntsIcon } from "./icons";

export const STORAGE_KEY = "explore_category";
const CATEGORY_BUTTON_CLASSNAME =
  "aspect-[277/110] rounded-[10px] border-rose-red aria-selected:border-2 aria-selected:shadow-0-4-30 aria-selected:shadow-rose-red/40 hover:shadow-0-0-20 hover:shadow-white/25";

export default function ExploreCategoryModal() {
  const [category, setCategory] = useState<string>("");
  const [isOpen, setIsOpen] = useState(false);

  const { track } = useAnalytics();
  const { isUser } = useAuth();

  const showModal = useReactiveVar(exploreCategoryModal);
  const search = useSearch({ strict: false }) as Record<
    string,
    string | undefined
  >;
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const isSearch = useMemo(() => pathname.startsWith("/explore"), [pathname]);

  const saveCategory = (newCategory: string) => {
    exploreCategory(newCategory);
    localStorage.setItem(STORAGE_KEY, newCategory);
  };

  const redirectToCategory = (newCategory: string) => {
    if (search.categories && search.categories === newCategory) {
      return;
    }

    const newSearch = { ...search };

    if (!newCategory || newCategory === CATEGORY_ALL) {
      if (!newSearch.categories) {
        return;
      }

      newSearch.categories = undefined;
    } else {
      newSearch.categories = newCategory;
    }

    navigate({ replace: true, search: newSearch, to: pathname });
  };

  const handleClick = (newCategory: string) => () => {
    setCategory(newCategory === category ? "" : newCategory);
  };

  const handleClose = (open: boolean) => {
    if (open) {
      return;
    }
    if (!category) {
      return;
    }

    saveCategory(category);
    track("Set Experience Preference", {
      category:
        category === CATEGORY_ALL
          ? [CATEGORY_ESCAPE_ROOM, CATEGORY_HAUNT]
          : [category],
    });

    if (isSearch) {
      redirectToCategory(category);
    }

    setIsOpen(false);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: redirectToCategory wont change
  useEffect(() => {
    if (isSearch) {
      const urlCategories = search.categories?.split(",") ?? [];

      let selectedCategory = CATEGORY_ALL;
      if (urlCategories.includes(CATEGORY_ESCAPE_ROOM)) {
        selectedCategory = CATEGORY_ESCAPE_ROOM;
      } else if (urlCategories.includes(CATEGORY_HAUNT)) {
        selectedCategory = CATEGORY_HAUNT;
      }

      (isUser ? saveCategory : exploreCategory)(selectedCategory);
    } else {
      const saved = localStorage.getItem(STORAGE_KEY) || CATEGORY_ALL;

      (isUser ? saveCategory : exploreCategory)(saved);

      if (isSearch) {
        redirectToCategory(saved);
      }
    }
  }, [search, isSearch, isUser]);

  useEffect(() => {
    if (showModal) {
      setIsOpen(true);
      exploreCategoryModal(false);
    }
  }, [showModal]);

  return (
    <Dialog onOpenChange={handleClose} open={isOpen}>
      <DialogContent
        className="w-xs gap-0 rounded-[25px] bg-[url(/images/main-bg.jpg)] bg-cover bg-no-repeat"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        overlayClassName="bg-graphite/90 items-center"
      >
        <DialogHeader className="border-navy-gray border-b">
          <DialogTitle className="text-center font-semibold text-16/[64px] text-white">
            Choose your adventures
          </DialogTitle>
        </DialogHeader>
        <div className="px-6 pt-6">
          <p className="text-center text-13 text-white">
            Which experiences are you into?
          </p>
          <div className="mt-3 flex flex-col items-stretch gap-5">
            <Button
              aria-label="Escape rooms"
              className={CATEGORY_BUTTON_CLASSNAME}
              onClick={handleClick(CATEGORY_ESCAPE_ROOM)}
              {...(category === CATEGORY_ESCAPE_ROOM && {
                "aria-selected": true,
              })}
            >
              <EscapeRoomsIcon className="size-full" />
            </Button>
            <Button
              aria-label="Haunts"
              className={CATEGORY_BUTTON_CLASSNAME}
              onClick={handleClick(CATEGORY_HAUNT)}
              {...(category === CATEGORY_HAUNT && { "aria-selected": true })}
            >
              <HauntsIcon className="size-full" />
            </Button>
            <Button
              aria-label="Both"
              className={CATEGORY_BUTTON_CLASSNAME}
              onClick={handleClick(CATEGORY_ALL)}
              {...(category === CATEGORY_ALL && { "aria-selected": true })}
            >
              <BothIcon className="size-full" />
            </Button>
          </div>
        </div>
        <DialogFooter className="p-6">
          <Button
            className="h-12.5 w-full rounded-[25px] text-16 transition-opacity disabled:opacity-30"
            disabled={category === ""}
            onClick={() => handleClose(false)}
            variant="cta"
          >
            Let’s do this!
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
